import { headerBreak } from '@/components/content/Header/styles/break';
import { SxProps, Theme } from '@mui/material';
import { OpenSansFontWeights } from 'shared/custom/constants/commonConstants';

export const departmentsOptionHeaderSX = (theme: Theme, isActive: boolean): SxProps => ({
	display: 'flex',
	cursor: 'pointer',
	borderRadius: '3px',
	height: '44px',
	justifyContent: 'center',
	zIndex: 1000,
	padding: '10px',
	backgroundColor: isActive ? 'grey.50' : 'white',
	'&:hover': {
		backgroundColor: 'grey.50',
	},
	'&:active': {
		backgroundColor: 'grey.50',
		color: 'primary.main',
	},
	[theme.breakpoints.down('md')]: {
		'.MuiStack-root': {
			display: 'none',
		},
	},
	[theme.breakpoints.up('xl')]: {
		alignItems: 'center',
		gap: '10px',
	},
	[theme.breakpoints.between('sm', 'lg')]: {
		alignItems: 'center',
		gap: '10px',
	},
	[theme.breakpoints.between('lg', 'xl')]: {
		display: 'flex',
		flexDirection: 'column',
		gap: '2px',
		alignItems: 'center',
		' p': {
			paddingTop: '0',
			paddingLeft: '0',
		},
	},
});

export const departmentsIconHeaderSx = { width: '24px', height: '24px' };

export const departmentsOptionLabelHeaderSX = (theme: Theme): SxProps => ({
	display: headerBreak({ xs: 'none', mobile: 'none', desktop: 'block' }),
	fontSize: '12px',
	fontWeight: OpenSansFontWeights.regular,
	color: 'grey.500',
	pointerEvents: 'none',
	'&:active': {
		color: 'grey.500',
	},
	[theme.breakpoints.up('md')]: {
		display: 'block',
		fontSize: '15px',
	},
	[theme.breakpoints.up('lg')]: {
		fontSize: '12px',
	},
	[theme.breakpoints.up('xl')]: {
		fontSize: '15px',
	},
});

export const styledMenuItemSX = (theme: Theme): SxProps<Theme> => ({
	display: 'flex',
	alignItems: 'center',
	width: '100%',
	fontSize: '15px',
	'&.MuiButtonBase-root.MuiMenuItem-root': {
		minHeight: '45px',
	},
	color: 'grey.500',
	padding: '10px 20px',
	borderBottom: `1px solid ${theme.palette.grey[50]}`,
	'& svg': {
		minWidth: '24px',
		fontWeight: '250',
	},
	'& .MuiListItemText-root': {
		padding: '0px',
		margin: '0px',
	},
});

export const styledBackButtonItemSX = (theme: Theme): SxProps<Theme> => ({
	...styledMenuItemSX(theme),
	gap: '5px',
	color: 'secondary.main',
	border: 'none',
	fontWeight: OpenSansFontWeights.regular,
	cursor: 'pointer',
	[theme.breakpoints.down('md')]: {
		padding: '15px 20px',
	},
});

export const styledDepartmentItemSX = (theme: Theme): SxProps<Theme> => ({
	...styledMenuItemSX(theme),
	gap: '15px',
	zIndex: 5000,
	'&:hover': {
		backgroundColor: 'grey.50',
		'& svg': {
			color: 'primary.main',
		},
	},
	'&:focus svg': {
		color: 'primary.main',
	},
});

export const styledSelectedDepartmentSX = (theme: Theme): SxProps<Theme> => ({
	...styledDepartmentItemSX(theme),
	gap: '5px',
	[theme.breakpoints.down('lg')]: {
		padding: '0px 20px 10px',
	},
	[theme.breakpoints.up('lg')]: {
		padding: '15px 20px',
	},
	backgroundColor: 'white',
	borderColor: 'grey.100',
	'& svg': {
		color: 'primary.main',
	},
	'&:hover': {
		backgroundColor: 'white',
	},
});

export const styledCategoryItemSX = (theme: Theme): SxProps<Theme> => ({
	...styledMenuItemSX(theme),
	justifyContent: 'space-between',
	'&:hover': {
		backgroundColor: 'white',
		'& .MuiListItemText-root': {
			textDecoration: 'underline',
			overflow: 'hidden',
			whiteSpace: 'nowrap',
			textOverflow: 'ellipsis',
		},
		'& svg': {
			color: 'grey.200',
			display: 'block',
		},
	},
	'&:focus svg': {
		color: 'primary.main',
	},
	'& svg': {
		display: 'none',
	},
});

export const styledSubCategoryItemSX = (theme: Theme): SxProps<Theme> => ({
	...styledMenuItemSX(theme),
	fontSize: '15px',
	fontWeight: OpenSansFontWeights.regular,
	color: 'gray.500',
	textDecoration: 'none',
	cursor: 'pointer',
	'& svg': {
		color: 'grey.500',
	},
	'&:hover': {
		backgroundColor: 'white',
		'& .MuiListItemText-root': {
			textDecoration: 'underline',
		},
		'& svg': {
			color: 'primary.main',
		},
	},
});

export const styledEMarketingSpotSX: SxProps<Theme> = {
	height: '100%',
	maxHeight: { xs: '200px', lg: '0%' },
	overflow: { xs: 'scroll', lg: 'unset' },
};

export const departmentsOptionMobileSX: SxProps<Theme> = {
	'& .MuiPaper-root': {
		maxHeight: '80vh',
		bottom: '60px',
	},
};

export const departmentsContentContainerSX: SxProps<Theme> = {
	overflowY: 'scroll',
	WebkitOverflowScrolling: 'touch',
};

export const eSpotContainerSX = { height: { xs: '200px', md: '100%' } };

export const eSpotRegisterContainerSX = (theme: Theme, isDialogView: boolean): SxProps<Theme> => ({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	height: 'auto',
	[theme.breakpoints.up('sm')]: {
		width: '100%',
	},
	[theme.breakpoints.up('md')]: {
		width: !isDialogView ? '50%' : '100%',
		borderLeft: '1px solid',
		borderLeftColor: 'backgroundGray',
	},
});

export const departmentsOptionStylesSX: Record<string, SxProps> = {
	listItemText: {
		fontSize: '15px',
		fontWeight: OpenSansFontWeights.regular,
		zIndex: 3000,
	},
	viewCategory: {
		fontSize: '15px',
		fontWeight: OpenSansFontWeights.regular,
		color: 'secondary.main',
		textDecoration: 'none',
	},
	goBackText: {
		fontSize: '12px',
		fontWeight: OpenSansFontWeights.regular,
		cursor: 'pointer',
		my: '4px',
	},
	categoriesItemText: {
		fontSize: '15px',
		fontWeight: OpenSansFontWeights.semibold,
	},
	previousLevelIcon: {
		color: 'primary.main',
		fontSize: '24px',
	},
	closeButton: {
		width: '24px',
		height: '24px',
		padding: 0,
		borderRadius: 'calc(infinity * 1px)',
		backgroundColor: 'inherit !important',
		border: 'none !important',
	},
	closeIcon: {
		color: 'grey.200',
		cursor: 'pointer',
	},
	previousLevelText: {
		fontSize: '12px',
		cursor: 'pointer',
		flexGrow: 1,
	},
	departmentsIconMobile: {
		color: 'primary.main',
		zIndex: 3000,
	},
	eSpotContainer: {
		display: 'flex',
		flexDirection: 'row',
	},
	menuTextTrigger: {
		pointerEvents: 'none',
	},
};

export const departmentsOptionMobileStylesSX: Record<string, (theme: Theme) => SxProps<Theme>> = {
	menuWidth: ({ breakpoints }) => ({
		[breakpoints.down('lg')]: {
			width: '100%',
		},
		[breakpoints.up('lg')]: {
			width: '385px',
		},
	}),
	categoriesMenuWidth: ({ breakpoints }) => ({
		[breakpoints.down('lg')]: {
			width: '100%',
		},
		[breakpoints.up('lg')]: {
			width: '307px',
		},
	}),
};
